import useTvApi from "hooks/useTvApi";
import React, { useEffect, useState } from "react";
import { TvDevice } from "./tvDevices";
import styles from "./TvTargetPicker.module.scss";

/**
 * Moskeens TV-er som kan velges for plakater og tidsstyrte visninger: de som
 * er koblet til eller venter på koden (ikke frakoblede eller utgåtte).
 */
export const useTargetableTvs = (orgNr?: string | null) => {
  const api = useTvApi();
  const [devices, setDevices] = useState<TvDevice[]>([]);
  useEffect(() => {
    if (!orgNr) return;
    let cancelled = false;
    api<TvDevice[]>(`/api/v1/mosques/${orgNr}/tv-devices`, "GET")
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        setDevices(
          list
            .filter((d) => d.status === "active" || d.status === "pending")
            .sort((a, b) => a.name.localeCompare(b.name, "nb")),
        );
      })
      .catch(() => !cancelled && setDevices([]));
    return () => {
      cancelled = true;
    };
  }, [api, orgNr]);
  return devices;
};

/** Valgte TV-er som fortsatt finnes. Tom = alle TV-ene. */
export const liveTargets = (ids: number[] | undefined, devices: TvDevice[]) =>
  (ids ?? []).filter((id) => devices.some((d) => d.id === id));

interface Props {
  devices: TvDevice[];
  /** Valgte TV-er; tom = alle. */
  value: number[] | undefined;
  onChange: (ids: number[]) => void;
  label?: string;
  disabled?: boolean;
}

/**
 * «Alle TV-er» eller enkelte TV-er, som knapper. Vises bare når moskeen har
 * mer enn én TV.
 */
const TvTargetPicker = ({
  devices,
  value,
  onChange,
  label = "TV-er",
  disabled,
}: Props) => {
  if (devices.length < 2) return null;
  const selected = liveTargets(value, devices);
  const all = selected.length === 0;
  const toggle = (id: number) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    // Alle valgt er det samme som «Alle TV-er» (og tar med TV-er som kommer til senere).
    onChange(next.length === devices.length ? [] : next);
  };
  // TV-er serveren ikke kan skille fra hverandre (samme nett og samme app) viser alt.
  const unsure = all
    ? []
    : devices.filter((d) => d.status === "active" && d.recognizable === false);
  return (
    <div className={styles.picker}>
      <div className={styles.chips} role="group" aria-label={label}>
        <button
          type="button"
          className={styles.chip}
          aria-pressed={all}
          disabled={disabled}
          onClick={() => onChange([])}
        >
          Alle TV-er
        </button>
        {devices.map((d) => (
          <button
            key={d.id}
            type="button"
            className={styles.chip}
            aria-pressed={selected.includes(d.id)}
            disabled={disabled}
            onClick={() => toggle(d.id)}
          >
            {d.name}
          </button>
        ))}
      </div>
      {unsure.length > 0 && (
        <p className={styles.note}>
          {unsure.map((d) => `«${d.name}»`).join(", ")} står på samme nett og er
          samme type TV, så de kan ikke skilles fra hverandre ennå. De viser alt
          til TV-appen er oppdatert.
        </p>
      )}
    </div>
  );
};

export default TvTargetPicker;
